import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// Optimistic locking is the same contract orders use: If-Match carries
// purchase_orders.version and a stale one is a 409 with the current version in the
// body. Without it the server answers 428 — the header is not optional.
const auth = (token, version) => ({
  headers: {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(version === undefined || version === null ? {} : { "If-Match": version }),
  },
});

// -- suppliers ----------------------------------------------------------------

export const listSuppliers = (activeOnly = true, token) =>
  axios
    .get(`${API}/purchase/suppliers`, { ...auth(token), params: { active_only: activeOnly } })
    .then(r => r.data);

export const createSupplier = (body, token) =>
  axios.post(`${API}/purchase/suppliers`, body, auth(token)).then(r => r.data);

export const updateSupplier = (supplierId, patch, token) =>
  axios.patch(`${API}/purchase/suppliers/${supplierId}`, patch, auth(token)).then(r => r.data);

// -- purchase orders ----------------------------------------------------------

export const listOrders = ({ status = null, supplierId = null, departmentId = null, kind = null, limit = 100 } = {}, token) =>
  axios
    .get(`${API}/purchase/orders`, {
      ...auth(token),
      params: {
        ...(status ? { status } : {}),
        ...(supplierId ? { supplier_id: supplierId } : {}),
        ...(departmentId ? { department_id: departmentId } : {}),
        ...(kind ? { kind } : {}),
        limit,
      },
    })
    .then(r => r.data);

/**
 * Create a draft.
 *
 * The body carries supplier, lines and dates only. `subtotal`, `tax_amount`,
 * `total` and `status` are computed server-side and the write schema sets
 * extra="forbid", so sending any of them is a 422 — a client that could name a
 * total could name what the restaurant owes a supplier.
 */
export const createOrder = (body, token) =>
  axios.post(`${API}/purchase/orders`, body, auth(token)).then(r => r.data);

export const getOrder = (poId, token) =>
  axios.get(`${API}/purchase/orders/${poId}`, auth(token)).then(r => r.data);

/** Sends the order to the supplier. Moves no stock — only receiving does. */
export const placeOrder = (poId, version, token) =>
  axios.post(`${API}/purchase/orders/${poId}/place`, {}, auth(token, version)).then(r => r.data);

/**
 * Book a delivery. Partial is the normal case: send only the lines that arrived,
 * with the quantity that arrived. The resulting status (partially_received or
 * received) is computed by the server from the running totals.
 */
export const receiveOrder = (poId, lines, version, token) =>
  axios
    .post(`${API}/purchase/orders/${poId}/receive`, { lines }, auth(token, version))
    .then(r => r.data);

/** Refused with a 400 once anything has been received against the order. */
export const cancelOrder = (poId, version, token) =>
  axios.post(`${API}/purchase/orders/${poId}/cancel`, {}, auth(token, version)).then(r => r.data);

// -- payments and payables ----------------------------------------------------

export const listPayments = ({ supplierId = null, limit = 100 } = {}, token) =>
  axios
    .get(`${API}/purchase/payments`, {
      ...auth(token),
      params: { ...(supplierId ? { supplier_id: supplierId } : {}), limit },
    })
    .then(r => r.data);

export const createPayment = (body, token) =>
  axios.post(`${API}/purchase/payments`, body, auth(token)).then(r => r.data);

/** Billed counts only orders goods have arrived against; a placed order is not a debt. */
export const listPayables = token =>
  axios.get(`${API}/purchase/payables`, auth(token)).then(r => r.data);

// -- purchase completion (2026-11-01) -------------------------------------------

/** Change a draft order; only the fields sent change. */
export const updateOrder = (poId, patch, version, token) =>
  axios.patch(`${API}/purchase/orders/${poId}`, patch, auth(token, version)).then(r => r.data);

/** A supplier bill entered as bought: stock in at once, paid now if `paid_amount` is set. */
export const createDirect = (body, token) =>
  axios.post(`${API}/purchase/direct`, body, auth(token)).then(r => r.data);

// Owner brief 2026-11-01: payment sources, voids, buy units.
export const listPaymentSources = token =>
  axios.get(`${API}/purchase/payment-sources`, auth(token)).then(r => r.data);

export const voidPayment = (paymentId, reason, token) =>
  axios.post(`${API}/purchase/payments/${paymentId}/void`, { reason }, auth(token)).then(r => r.data);

export const voidBill = (poId, reason, version, token) =>
  axios.post(`${API}/purchase/orders/${poId}/void`, { reason }, { headers: { ...auth(token).headers, "If-Match": String(version) } })
    .then(r => r.data);

export const listBuyUnits = (itemId, token) =>
  axios.get(`${API}/inventory/items/${itemId}/buy-units`, auth(token)).then(r => r.data);

export const listDepartments = token =>
  axios.get(`${API}/purchase/departments`, auth(token)).then(r => r.data);

export const createDepartment = (body, token) =>
  axios.post(`${API}/purchase/departments`, body, auth(token)).then(r => r.data);

export const updateDepartment = (deptId, patch, token) =>
  axios.patch(`${API}/purchase/departments/${deptId}`, patch, auth(token)).then(r => r.data);

export const listReturns = ({ supplierId = null, limit = 100 } = {}, token) =>
  axios
    .get(`${API}/purchase/returns`, { ...auth(token), params: { ...(supplierId ? { supplier_id: supplierId } : {}), limit } })
    .then(r => r.data);

export const createReturn = (body, token) =>
  axios.post(`${API}/purchase/returns`, body, auth(token)).then(r => r.data);

/** One bill/invoice file; `parent` is { orderId } or { returnId }. */
export const uploadAttachment = (parent, file, token) => {
  const form = new FormData();
  form.append("file", file, file.name);
  const path = parent.orderId ? `orders/${parent.orderId}` : `returns/${parent.returnId}`;
  return axios.post(`${API}/purchase/${path}/attachments`, form, auth(token)).then(r => r.data);
};

/** The file's bytes as a Blob (the request carries the login, so a plain link cannot). */
export const fetchAttachment = (attachmentId, token) =>
  axios.get(`${API}/purchase/attachments/${attachmentId}`, { ...auth(token), responseType: "blob" }).then(r => r.data);

export const deleteAttachment = (attachmentId, token) =>
  axios.delete(`${API}/purchase/attachments/${attachmentId}`, auth(token)).then(r => r.data);

export const supplierLedger = (supplierId, { fromDate = null, toDate = null } = {}, token) =>
  axios
    .get(`${API}/purchase/suppliers/${supplierId}/ledger`, {
      ...auth(token), params: { ...(fromDate ? { from_date: fromDate } : {}), ...(toDate ? { to_date: toDate } : {}) },
    })
    .then(r => r.data);

export const purchaseReport = ({ fromDate, toDate, group }, token) =>
  axios
    .get(`${API}/purchase/report`, { ...auth(token), params: { from_date: fromDate, to_date: toDate, group } })
    .then(r => r.data);

/** What a vendor supplies, with the last rate (2026-11-01). */
export const listSupplierItems = (supplierId, token) =>
  axios.get(`${API}/purchase/suppliers/${supplierId}/items`, auth(token)).then(r => r.data);

/** Replace a vendor's item list: [{ stock_item_id, last_unit_cost? }]. */
export const setSupplierItems = (supplierId, items, token) =>
  axios.put(`${API}/purchase/suppliers/${supplierId}/items`, { items }, auth(token)).then(r => r.data);
